import { AuthFetch } from '@moonpig/web-core-auth'
import { logger } from '@moonpig/web-core-monitoring'
import { Region } from '@moonpig/web-core-types'
import {
  AccountLookupResult,
  AccountLookupResponse,
  AuthenticationServiceType,
  AccountLookupInput,
  LoginInput,
  LoginResult,
  RegisterInput,
  RegisterResult,
  ValidateEmailInput,
  ValidateEmailResult,
  ValidateEmailResponse,
  AccountLookupApiResponse,
  MagicLinkLoginResult,
  LoginErrorCodes,
  SendLoginCodeResult,
  SendLoginCodeInput,
  LoginWithCodeInput,
  PasswordSetResult,
  RegisterWithCodeInput,
} from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parse400Errors = (json: any): { field: string; message: string }[] => {
  const registrationErrors: Array<{
    field: string
    message: string
  }> = []

  const {
    invalidInputEmail,
    invalidInputPassword,
    invalidInputFirstName,
    invalidInputLastName,
    invalidTermsAndConditions,
    accountExists,
  } = json

  /* istanbul ignore else */
  if (invalidInputEmail)
    registrationErrors.push({ field: 'email', message: invalidInputEmail })
  /* istanbul ignore else */
  if (accountExists)
    registrationErrors.push({ field: 'email', message: accountExists })
  /* istanbul ignore else */
  if (invalidInputPassword)
    registrationErrors.push({
      field: 'password',
      message: invalidInputPassword,
    })
  /* istanbul ignore else */
  if (invalidInputFirstName)
    registrationErrors.push({
      field: 'firstName',
      message: invalidInputFirstName,
    })
  /* istanbul ignore else */
  if (invalidInputLastName)
    registrationErrors.push({
      field: 'lastName',
      message: invalidInputLastName,
    })
  /* istanbul ignore else */
  if (invalidTermsAndConditions)
    registrationErrors.push({
      field: 'termsAccepted',
      message: invalidTermsAndConditions,
    })

  return registrationErrors
}

export const authenticationService = (
  authFetch: AuthFetch,
  region: Region,
): AuthenticationServiceType => {
  const accountLookup = async ({
    email,
  }: AccountLookupInput): Promise<AccountLookupResult> => {
    try {
      const response = await authFetch({
        path: '/account-lookup',
        body: JSON.stringify({ email }),
      })
      switch (response.status) {
        case 200: {
          const apiResponse: AccountLookupApiResponse = await response.json()

          if (apiResponse.state === 'inactive') {
            return { response: AccountLookupResponse.ACCOUNT_DEACTIVATED }
          }

          return {
            response: apiResponse.password
              ? AccountLookupResponse.ACCOUNT_EXISTS_WITH_PASSWORD
              : AccountLookupResponse.ACCOUNT_EXISTS_WITHOUT_PASSWORD,
          }
        }
        case 400: {
          const json = await response.json()
          return {
            response: AccountLookupResponse.ERROR,
            errorMessage: json.errorMessage,
          }
        }
        case 404:
          return { response: AccountLookupResponse.ACCOUNT_NOT_EXIST }

        default:
          logger.fixToday(`Failed to lookup account (unknown code)`, {
            responseStatusCode: response.status,
          })
          return { response: AccountLookupResponse.ERROR }
      }
    } catch (error) {
      logger.fixToday('Failed to lookup account (exception)', undefined, error)
      return { response: AccountLookupResponse.ERROR }
    }
  }

  const login = async ({
    email,
    password,
    flowId,
  }: LoginInput): Promise<LoginResult> => {
    try {
      const response = await authFetch({
        path: '/login',
        body: JSON.stringify({ email, password, region, flowId }),
      })
      if (response.ok) {
        return {
          success: true,
        }
      }

      const json = await response.json()

      switch (response.status) {
        case 400:
        case 401:
          return {
            success: false,
            errorMessage: json.errorMessage,
          }
        case 403:
          return {
            success: false,
            errorMessage: json.errorMessage,
            accountLocked: json.errorCode !== LoginErrorCodes.IDENTITY_INACTIVE,
          }
        default:
          logger.fixToday('Failed to login', {
            response: JSON.stringify(json),
            statusCode: response.status,
          })
          return {
            success: false,
            errorMessage: json.errorMessage,
          }
      }
    } catch (error) {
      logger.fixToday('Failed to login (exception)', undefined, error)
      return {
        success: false,
      }
    }
  }

  const register = async ({
    email,
    password,
    firstName,
    lastName,
    termsAccepted,
    source,
  }: RegisterInput): Promise<RegisterResult> => {
    try {
      const response = await authFetch({
        path: '/register',
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          termsAccepted,
          region,
          source,
        }),
      })

      if (response.status === 200) {
        return {
          success: true,
        }
      }

      const json = await response.json()

      switch (response.status) {
        case 400:
        case 409:
          return {
            success: false,
            errors: parse400Errors(json),
          }
        default:
          logger.fixToday(`Failed to create account (unknown code)`, {
            responseStatusCode: response.status,
          })
          return {
            success: false,
          }
      }
    } catch (error) {
      logger.fixToday('Failed to create account (exception)', undefined, error)
      return {
        success: false,
      }
    }
  }

  const validateEmail = async ({
    email,
  }: ValidateEmailInput): Promise<ValidateEmailResult> => {
    try {
      const response = await authFetch({
        path: '/validate-email',
        body: JSON.stringify({ email }),
      })
      switch (response.status) {
        case 200:
          return {
            response: ValidateEmailResponse.EMAIL_VALID,
          }
        case 400: {
          return {
            response: ValidateEmailResponse.EMAIL_INVALID,
          }
        }
        default:
          logger.fixToday(`Failed to validate email`, {
            responseStatusCode: response.status,
          })
          return { response: ValidateEmailResponse.ERROR }
      }
    } catch (error) {
      logger.fixToday('Failed to validate email (exception)', undefined, error)
      return { response: ValidateEmailResponse.ERROR }
    }
  }

  const magicLinkLogin = async (
    token: string,
  ): Promise<MagicLinkLoginResult> => {
    try {
      const response = await authFetch({
        path: '/magic/login',
        body: JSON.stringify({ token }),
      })

      if (response.ok) {
        return { success: true, skipped: response.status === 204 }
      }

      const { errorMessage } = await response.json()
      return { success: false, errorMessage }
    } catch (error) {
      return { success: false }
    }
  }

  const sendLoginCode = async ({
    email,
    action,
    flowId,
  }: SendLoginCodeInput): Promise<SendLoginCodeResult> => {
    try {
      const result = await authFetch({
        path: '/code/generate',
        body: JSON.stringify({ email, action, flowId }),
      })

      const response = await result.json()

      if (!response.flowId) {
        const { errorCode, errorMessage } = response

        return { errorCode, errorMessage }
      }

      return { flowId: response.flowId }
    } catch (error) {
      const errorMessage = 'Failed to send login code (exception)'
      logger.fixToday(errorMessage, undefined, error)
      return {
        errorCode: error.message,
        errorMessage,
      }
    }
  }

  const loginWithCode = async ({
    flowId,
    email,
    code,
  }: LoginWithCodeInput): Promise<LoginResult> => {
    try {
      const response = await authFetch({
        path: '/code/login',
        body: JSON.stringify({ flowId, email, code }),
      })

      if (!response.ok) {
        const json = await response.json()

        return {
          success: false,
          errorTitle: json.errorTitle,
          errorMessage: json.errorMessage,
          accountLocked: response.status === 403,
          errorCode: json.errorCode,
        }
      }

      return {
        success: true,
      }
    } catch (error) {
      logger.fixToday('Failed to login with code (exception)', undefined, error)
      return {
        success: false,
      }
    }
  }

  const registerWithCode = async ({
    flowId,
    email,
    code,
    firstName,
    lastName,
  }: RegisterWithCodeInput): Promise<RegisterResult> => {
    try {
      const response = await authFetch({
        path: '/code/register',
        body: JSON.stringify({ flowId, email, code, firstName, lastName }),
      })

      if (!response.ok) {
        const json = await response.json()

        return {
          success: false,
          errorTitle: json.errorTitle,
          errorMessage: json.errorMessage,
          errorCode: json.errorCode,
        }
      }

      return {
        success: true,
      }
    } catch (error) {
      logger.fixToday('Failed to login with code (exception)', undefined, error)
      return {
        success: false,
      }
    }
  }

  const passwordSet = async (
    newPassword: string,
  ): Promise<PasswordSetResult> => {
    try {
      const result = await authFetch({
        path: '/password/set',
        body: JSON.stringify({ newPassword }),
      })

      if (!result.ok) {
        const { errorMessage } = await result.json()

        return {
          success: false,
          errorMessage,
        }
      }

      return {
        success: true,
      }
    } catch (error) {
      logger.fixToday('Failed to set password (exception)', undefined, error)
      return {
        success: false,
      }
    }
  }

  return {
    accountLookup,
    login,
    register,
    validateEmail,
    magicLinkLogin,
    sendLoginCode,
    loginWithCode,
    registerWithCode,
    passwordSet,
  }
}
